import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type SyntheticEvent } from 'react';
import { addDays, mondayOnOrBefore } from '../../../lib/dates/iso.ts';
import { describeDays, formatDate, formatDayYear, formatPeriod, formatRange } from '../../../lib/format/dates.ts';
import { EMPTY_CELL, cellKey, cellStates, type CellState, type EntryKind } from '../../../lib/requests/cells.ts';
import { defaultDueDate, defaultPeriodName, defaultPeriodStart } from '../../../lib/requests/periods.ts';
import { suggestedReopenDueDate } from '../../../lib/requests/reopen.ts';
import { DEFAULT_PERIOD_MONTHS, PERIOD_MONTHS, periodEnd, type PeriodMonths } from '../../../lib/weeks/weeks.ts';
import { api, type AvailabilityCell, type Physician, type PhysicianEntry, type PlainWeek, type RequestPeriod, type RequestStatus, type RequestView } from '../../api.ts';
import { Notice } from '../../components/Notice.tsx';
import { copy } from '../../copy/en-CA.ts';
import { describeAvailabilityFailure as describe } from '../../describe.ts';
import { useSession } from '../../session.tsx';

const c = copy.admin.availability;
const kindsLong = copy.requests.period.kindsLong;
/** The admin entry's choices, in the order the window offers them; null is None. */
const ENTRY_CHOICES: (EntryKind | null)[] = ['vacation', 'loa', 'preferred', 'avoid', null];

interface PeriodInput {
  start: string;
  end: string;
  dueDate: string;
  name: string;
}

/** A short message next to the request periods: where it shows, and what it says. */
interface PeriodsNotice {
  kind: 'success' | 'error';
  text: string;
  /** Under the periods table, or under the form (after opening one), next to the button pressed. */
  at: 'table' | 'form';
}

function statusBadgeClass(status: RequestStatus): string {
  return status === 'approved' ? 'ok' : status === 'declined' ? 'conf' : 'pending';
}

function cellId(physicianId: string, weekStart: string): string {
  return `cell-${physicianId}-${weekStart}`;
}

/** Everything a week holds, in words, with where each part came from: the cell's accessible name. */
function cellStateText(state: CellState): string {
  const parts: string[] = [];
  if (state.entry) parts.push(c.cellState.entry(kindsLong[state.entry]));
  for (const r of state.requests) parts.push(c.cellState.request(kindsLong[r.kind], c.cellState.status[r.status]));
  return parts.length > 0 ? parts.join('; ') : c.cellState.none;
}

/** The classes that draw a cell: its kind's colour, how it came about, and the mark for an entry over a request. */
function cellClass(state: CellState): string {
  const cls = ['avail-cell'];
  const shown = state.shown;
  if (shown) {
    // A declined request is drawn without its kind's colour: it does not count.
    cls.push(shown.source === 'declined' ? 'declined' : shown.kind);
    if (shown.source === 'pending') cls.push('pending');
    if (shown.source === 'approved') cls.push('from-request');
  }
  if (state.overRequest) cls.push('over-request');
  return cls.join(' ');
}

/**
 * Opening a request period. The start begins at the Monday after the latest
 * existing period (periods.ts), so the defaults never repeat one already
 * open; the due date follows the start until the admin sets one. A name left
 * blank is sent as the placeholder shows it.
 */
function PeriodForm({ defaultStart, today, error, onOpen }: { defaultStart: string; today: string; error: string | null; onOpen: (input: PeriodInput) => Promise<boolean> }) {
  const [name, setName] = useState('');
  const [start, setStart] = useState(defaultStart);
  const [months, setMonths] = useState<PeriodMonths | 'custom'>(DEFAULT_PERIOD_MONTHS);
  const [customEnd, setCustomEnd] = useState('');
  // Null while the admin has not chosen one: then it follows the start.
  const [due, setDue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // When the default moves (a period was opened or deleted), the form moves with it.
  const [seenDefault, setSeenDefault] = useState(defaultStart);
  if (seenDefault !== defaultStart) {
    setSeenDefault(defaultStart);
    setStart(defaultStart);
    setDue(null);
  }

  const startMonday = start ? mondayOnOrBefore(start) : '';
  const end = months === 'custom' ? customEnd : startMonday ? periodEnd(startMonday, months) : '';
  const dueDate = due ?? (startMonday ? defaultDueDate(startMonday, today) : '');
  const shownName = startMonday && end ? defaultPeriodName(startMonday, end) : '';

  const submit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ok = await onOpen({ start: startMonday, end, dueDate, name: name.trim() || shownName });
      if (ok) setName('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={(e) => { void submit(e); }} aria-labelledby="open-period">
      <h3 id="open-period">{c.openPeriod}</h3>
      <div className="row">
        <div className="field grow">
          <label htmlFor="rp-name">{c.periodName}</label>
          <input id="rp-name" className="input" value={name} placeholder={shownName} onChange={(e) => { setName(e.target.value); }} />
        </div>
        <div className="field">
          <label htmlFor="rp-start">{c.periodStart}</label>
          <input id="rp-start" className="input" type="date" required value={start} onChange={(e) => { setStart(e.target.value); }} />
        </div>
        <div className="field">
          <label htmlFor="rp-months">{c.periodEnd}</label>
          <select id="rp-months" className="input" value={months} onChange={(e) => { setMonths(e.target.value === 'custom' ? 'custom' : (Number(e.target.value) as PeriodMonths)); }}>
            {PERIOD_MONTHS.map((m) => (
              <option key={m} value={m}>
                {copy.admin.build.months(m)}
              </option>
            ))}
            <option value="custom">{copy.admin.build.custom}</option>
          </select>
        </div>
        {months === 'custom' ? (
          <div className="field">
            <label htmlFor="rp-end">{copy.admin.build.end}</label>
            <input id="rp-end" className="input" type="date" required value={customEnd} onChange={(e) => { setCustomEnd(e.target.value); }} />
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="rp-due">{c.periodDue}</label>
          <input id="rp-due" className="input" type="date" required value={dueDate} onChange={(e) => { setDue(e.target.value); }} />
        </div>
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}
      <div>
        <button type="submit" className="btn primary" disabled={busy || !startMonday || !end}>
          {busy ? c.creating : c.create}
        </button>
      </div>
    </form>
  );
}

/**
 * Shown under the periods table when the admin presses Reopen: a new due
 * date (a closed period's own one has usually gone by), and a reminder that
 * nothing already submitted is lost. Once the period has started it cannot
 * take requests any more, and the panel says so instead.
 */
function ReopenPanel({ period, today, onCancel, onReopened }: { period: RequestPeriod; today: string; onCancel: () => void; onReopened: (period: RequestPeriod) => Promise<void> }) {
  const suggested = suggestedReopenDueDate(period.startDate, period.dueDate, today);
  const [dueDate, setDueDate] = useState(suggested ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // A keyboard user lands in the panel they just opened.
  useEffect(() => {
    (dateRef.current ?? cancelRef.current)?.focus();
  }, []);

  const headingId = `reopen-title-${period.id}`;
  const heading = <h3 id={headingId}>{c.reopenTitle(period.name)}</h3>;

  if (suggested === null) {
    return (
      <div className="stack reopen-panel" role="group" aria-labelledby={headingId}>
        {heading}
        <Notice kind="info">{c.reopenStarted}</Notice>
        <div>
          <button ref={cancelRef} type="button" className="btn small" onClick={onCancel}>
            {c.reopenCancel}
          </button>
        </div>
      </div>
    );
  }

  const submit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    let reopened: RequestPeriod;
    try {
      reopened = (await api.admin.updateRequestPeriod(period.id, { closed: false, dueDate })).period;
    } catch (err) {
      setError(describe(err));
      setBusy(false);
      return;
    }
    await onReopened(reopened);
  };

  const inputId = `reopen-due-${period.id}`;
  return (
    <form className="stack reopen-panel" aria-labelledby={headingId} onSubmit={(e) => { void submit(e); }}>
      {heading}
      <p>{c.reopenNote}</p>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div className="field">
          <label htmlFor={inputId}>{c.reopenDue}</label>
          <input
            ref={dateRef}
            id={inputId}
            className="input"
            type="date"
            required
            min={today}
            max={addDays(period.startDate, -1)}
            value={dueDate}
            onChange={(e) => { setDueDate(e.target.value); }}
          />
        </div>
        <button type="submit" className="btn primary small" disabled={busy || !dueDate}>
          {busy ? c.reopening : c.reopenConfirm}
        </button>
        <button type="button" className="btn small" onClick={onCancel}>
          {c.reopenCancel}
        </button>
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}
    </form>
  );
}

/**
 * What one grid cell opens: the week's two separate things, each with its own
 * explicit actions. The physician's request is approved or declined (as on the
 * review list); the admin entry is set to a kind or to None, which removes
 * only the entry. Nothing changes by opening it, so a stray second click on
 * the cell cannot change anything either. While a change saves, every button
 * is locked.
 */
function CellEditor({
  physicianName,
  week,
  state,
  mine,
  busy,
  error,
  onEntry,
  onDecide,
  onClose,
}: {
  physicianName: string;
  week: PlainWeek;
  state: CellState;
  mine: boolean;
  busy: boolean;
  error: string | null;
  onEntry: (kind: EntryKind | null) => void;
  onDecide: (requestId: string, status: RequestStatus) => void;
  onClose: () => void;
}) {
  const range = formatRange(week.onCallStart, week.onCallEnd);
  // The second click of a double click, landing on a button that appeared
  // under the pointer, is never taken as a choice (click count 2 or more).
  const once = (fn: () => void) => (e: MouseEvent<HTMLButtonElement>) => {
    if (e.detail > 1) return;
    fn();
  };
  return (
    <div className="stack">
      <div>
        {/* Focus starts here, not on a button, so a held Enter key cannot press one. */}
        <h2 id="cell-editor-title" tabIndex={-1}>
          {c.editor.title(physicianName, range)}
        </h2>
        <p className="muted small">{describeDays(week)}</p>
      </div>
      <section className="stack cell-editor-part" aria-labelledby="cell-editor-request">
        <h3 id="cell-editor-request">{mine ? c.editor.requestTitleYours : c.editor.requestTitle}</h3>
        {state.requests.length === 0 ? <p className="muted">{c.editor.noRequest}</p> : null}
        {state.requests.map((r) => (
          <div key={r.id} className="row">
            <span className="badges">
              <span className={`badge req-${r.kind}`}>{kindsLong[r.kind]}</span>
              <span className={`badge ${statusBadgeClass(r.status)}`}>{copy.requests.period.status[r.status]}</span>
            </span>
            {r.status !== 'approved' ? (
              <button type="button" className="btn small" disabled={busy} onClick={once(() => { onDecide(r.id, 'approved'); })}>
                {c.editor.approve}
              </button>
            ) : null}
            {r.status !== 'declined' ? (
              <button type="button" className="btn small" disabled={busy} onClick={once(() => { onDecide(r.id, 'declined'); })}>
                {c.editor.decline}
              </button>
            ) : null}
          </div>
        ))}
        {state.overRequest ? <p className="muted small">{c.editor.replaced}</p> : null}
      </section>
      <section className="stack cell-editor-part" aria-labelledby="cell-editor-entry">
        <h3 id="cell-editor-entry">{c.editor.entryTitle}</h3>
        <p className="muted small">{c.editor.entryHint}</p>
        <div className="seg-group" role="group" aria-labelledby="cell-editor-entry">
          {ENTRY_CHOICES.map((k) => {
            const on = state.entry === k;
            return (
              <button
                key={k ?? 'none'}
                type="button"
                aria-pressed={on}
                className={`seg small ${on ? `on ${k ?? 'none'}` : ''}`}
                disabled={busy}
                onClick={once(() => { onEntry(k); })}
              >
                {k ? c.legend[k] : c.editor.none}
              </button>
            );
          })}
        </div>
      </section>
      {error ? <Notice kind="error">{error}</Notice> : null}
      <div className="row">
        <button type="button" className="btn" disabled={busy} onClick={onClose}>
          {busy ? c.editor.saving : c.editor.close}
        </button>
        {/* Always present, so a screen reader hears "Saving…" when it appears. */}
        <span className="visually-hidden" role="status">
          {busy ? c.editor.saving : ''}
        </span>
      </div>
    </div>
  );
}

export function AdminAvailabilityPage() {
  const { user } = useSession();
  // The signed-in admin's own physician, if they are one: their rows say "You".
  const myPhysicianId = user?.physicianId ?? null;
  const [periods, setPeriods] = useState<RequestPeriod[] | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [reopening, setReopening] = useState<string | null>(null);
  const [suggested, setSuggested] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [review, setReview] = useState<{ requests: RequestView[]; entries: PhysicianEntry[] }>({ requests: [], entries: [] });
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [grid, setGrid] = useState<{ weeks: PlainWeek[]; physicians: Physician[]; cells: AvailabilityCell[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [periodsNotice, setPeriodsNotice] = useState<PeriodsNotice | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [gridError, setGridError] = useState<string | null>(null);
  // The grid cell whose window is open, and whether a change from it is saving.
  const [editing, setEditing] = useState<{ physician: Physician; week: PlainWeek } | null>(null);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  // The cell to give focus back to once its window has closed and it is no longer locked.
  const [focusCell, setFocusCell] = useState<string | null>(null);
  const savingRef = useRef(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Answers that arrive after a newer request was made are dropped, so a slow
  // answer for the old range or period never overwrites the new one.
  const gridSeq = useRef(0);
  const reviewSeq = useRef(0);

  const loadPeriods = useCallback(async () => {
    const [{ periods: list, today: serverToday }, { suggestedStart }] = await Promise.all([api.requestPeriods(), api.schedules()]);
    setPeriods(list);
    // The group's date (Toronto), from the server, not this device's clock.
    setToday(serverToday);
    setSuggested(suggestedStart);
    return { list, suggestedStart };
  }, []);

  useEffect(() => {
    loadPeriods()
      .then(({ list, suggestedStart }) => {
        const first = list.find((p) => p.open) ?? list[0] ?? null;
        setSelected(first?.id ?? null);
        const start = first?.startDate ?? suggestedStart ?? '';
        const end = first?.endDate ?? (start ? periodEnd(start, DEFAULT_PERIOD_MONTHS) : '');
        setRangeStart(start);
        setRangeEnd(end);
      })
      .catch((err: unknown) => {
        setError(describe(err));
      });
  }, [loadPeriods]);

  const loadReview = useCallback(async (periodId: string | null) => {
    const seq = ++reviewSeq.current;
    if (!periodId) {
      setReview({ requests: [], entries: [] });
      return;
    }
    try {
      const r = await api.admin.periodRequests(periodId);
      if (seq === reviewSeq.current) setReview(r);
    } catch (err) {
      if (seq === reviewSeq.current) setReviewError(describe(err));
    }
  }, []);

  useEffect(() => {
    void loadReview(selected);
  }, [selected, loadReview]);

  const loadGrid = useCallback(async (start: string, end: string) => {
    if (!start || !end || end < start) return;
    const seq = ++gridSeq.current;
    try {
      const g = await api.admin.availability(mondayOnOrBefore(start), end);
      if (seq !== gridSeq.current) return;
      setGrid(g);
      setGridError(null);
    } catch (err) {
      if (seq === gridSeq.current) setGridError(describe(err));
    }
  }, []);

  useEffect(() => {
    void loadGrid(rangeStart, rangeEnd);
  }, [rangeStart, rangeEnd, loadGrid]);

  /** Every action starts from a clean page: no message from an earlier one lingers. */
  const clearNotices = () => {
    setError(null);
    setPeriodsNotice(null);
    setFormError(null);
    setReviewError(null);
    setGridError(null);
  };

  /**
   * After a change: the periods, the review list and the grid. If the period
   * under review has gone (deleted), the next one is selected, and the grid
   * moves to its weeks with it, so the two never show different periods.
   */
  const refresh = async () => {
    try {
      const { list } = await loadPeriods();
      const kept = selected !== null && list.some((p) => p.id === selected);
      const nextPeriod = kept ? null : (list.find((p) => p.open) ?? list[0] ?? null);
      if (kept) {
        await Promise.all([loadGrid(rangeStart, rangeEnd), loadReview(selected)]);
        return;
      }
      // A new selection loads its own review list (an effect on `selected`).
      setSelected(nextPeriod?.id ?? null);
      if (nextPeriod && (nextPeriod.startDate !== rangeStart || nextPeriod.endDate !== rangeEnd)) {
        // So does a new range (an effect on the range).
        setRangeStart(nextPeriod.startDate);
        setRangeEnd(nextPeriod.endDate);
      } else {
        await loadGrid(rangeStart, rangeEnd);
      }
    } catch (err) {
      setError(describe(err));
    }
  };

  /** Close, reopen or delete: one short message under the periods table on success, or the reason it failed. */
  const periodAction = async (fn: () => Promise<unknown>, success: string) => {
    clearNotices();
    setReopening(null);
    try {
      await fn();
    } catch (err) {
      setPeriodsNotice({ kind: 'error', text: describe(err), at: 'table' });
      return;
    }
    setPeriodsNotice({ kind: 'success', text: success, at: 'table' });
    await refresh();
  };

  const openPeriod = async (input: PeriodInput): Promise<boolean> => {
    clearNotices();
    setReopening(null);
    let period: RequestPeriod;
    try {
      period = (await api.admin.createRequestPeriod(input)).period;
    } catch (err) {
      setFormError(describe(err));
      return false;
    }
    setPeriodsNotice({ kind: 'success', text: c.opened(period.name), at: 'form' });
    // The new period is the one to review, and the grid shows its weeks.
    setSelected(period.id);
    setRangeStart(period.startDate);
    setRangeEnd(period.endDate);
    try {
      await loadPeriods();
    } catch (err) {
      setError(describe(err));
    }
    return true;
  };

  /** Approve all, or one request's decision on the review list. Errors show beside the list. */
  const reviewAction = async (fn: () => Promise<unknown>) => {
    clearNotices();
    try {
      await fn();
    } catch (err) {
      setReviewError(describe(err));
      return;
    }
    await refresh();
  };

  const states = useMemo(() => cellStates(grid?.cells ?? []), [grid]);
  const entryOver = useMemo(() => new Map(review.entries.map((e) => [cellKey(e.physicianId, e.weekStart), e.kind])), [review.entries]);

  // The cell window is a native modal dialog: it keeps focus inside, closes on
  // Escape, and on a phone sits on top of the grid instead of off to its side.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!editing || !dialog || dialog.open) return;
    dialog.showModal();
    document.getElementById('cell-editor-title')?.focus();
  }, [editing]);

  const openCell = (physician: Physician, week: PlainWeek) => {
    if (savingRef.current) return;
    setEditorError(null);
    setEditing({ physician, week });
  };

  const closeCell = () => {
    dialogRef.current?.close();
  };

  // Called by the dialog's own close event (Escape, Close, or a change saved): back to the cell.
  const onDialogClosed = () => {
    if (editing) setFocusCell(cellId(editing.physician.id, editing.week.weekStart));
    setEditing(null);
    setEditorError(null);
  };

  useEffect(() => {
    if (focusCell === null || savingCell !== null) return;
    document.getElementById(focusCell)?.focus();
    setFocusCell(null);
  }, [focusCell, savingCell]);

  /** One change from the cell window, locked until it has saved and the grid shows it. */
  const saveCell = async (fn: () => Promise<unknown>) => {
    if (!editing || savingRef.current) return;
    savingRef.current = true;
    const key = cellKey(editing.physician.id, editing.week.weekStart);
    setSavingCell(key);
    setEditorError(null);
    clearNotices();
    try {
      await fn();
    } catch (err) {
      setEditorError(describe(err));
      savingRef.current = false;
      setSavingCell(null);
      return;
    }
    await refresh();
    savingRef.current = false;
    setSavingCell(null);
    closeCell();
  };

  const editingState = editing ? (states.get(cellKey(editing.physician.id, editing.week.weekStart)) ?? EMPTY_CELL) : EMPTY_CELL;
  const editingName = editing ? (editing.physician.id === myPhysicianId ? c.youName(editing.physician.name) : editing.physician.name) : '';

  const chooseEntry = (kind: EntryKind | null) => {
    if (!editing) return;
    // The current choice changes nothing.
    if (kind === editingState.entry) {
      closeCell();
      return;
    }
    // A leave is a hard block, and the week drops out of their fair share.
    if (kind === 'loa' && !window.confirm(c.editor.confirmLeave(editingName, formatRange(editing.week.onCallStart, editing.week.onCallEnd)))) return;
    const { physician, week } = editing;
    void saveCell(() => api.admin.setAvailability(physician.id, week.weekStart, kind));
  };

  const selectedPeriod = periods?.find((p) => p.id === selected) ?? null;
  const reopeningPeriod = periods?.find((p) => p.id === reopening) ?? null;
  const formDefaultStart = periods !== null ? defaultPeriodStart(periods, suggested) : null;
  const notice = periodsNotice ? <Notice kind={periodsNotice.kind}>{periodsNotice.text}</Notice> : null;

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{c.intro}</p>
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}

      <section className="card stack" aria-labelledby="periods-h">
        <h2 id="periods-h">{c.periods}</h2>
        {periods?.length === 0 ? <p className="muted">{c.noPeriods}</p> : null}
        {periods && periods.length > 0 ? (
          <div className="table-region" role="region" aria-label={c.periods} tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>{c.columns.period}</th>
                  <th>{c.columns.due}</th>
                  <th>{c.columns.submitted}</th>
                  <th>{c.columns.pending}</th>
                  <th>{c.columns.actions}</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.id} className={p.id === selected ? 'mine' : ''}>
                    <td>
                      <strong>{p.name}</strong>
                      <div className="muted small">{formatPeriod(p.startDate, p.endDate)}</div>
                    </td>
                    <td>
                      {formatDayYear(p.dueDate)}{' '}
                      {p.open ? <span className="badge physician">{copy.requests.open}</span> : <span className="badge inactive">{copy.requests.closed}</span>}
                    </td>
                    <td>{c.submittedLine(p.physiciansSubmitted, p.requestCount)}</td>
                    <td>{p.pendingCount > 0 ? <span className="badge pending">{p.pendingCount}</span> : <span className="muted">0</span>}</td>
                    <td>
                      <div className="row">
                        <button type="button" className="btn small" onClick={() => { setSelected(p.id); setRangeStart(p.startDate); setRangeEnd(p.endDate); }}>
                          {c.review}
                        </button>
                        {/* Open means taking requests: a period past its due date shows Reopen too, though nobody closed it by hand. */}
                        {p.open ? (
                          <button
                            type="button"
                            className="btn small"
                            onClick={() => {
                              // Requests still awaiting approval do not count; closing must not hide that.
                              if (p.pendingCount > 0 && !window.confirm(c.confirmClose(p.pendingCount))) return;
                              void periodAction(() => api.admin.updateRequestPeriod(p.id, { closed: true }), c.closedNotice(p.name));
                            }}
                          >
                            {c.close}
                          </button>
                        ) : (
                          <button
                            id={`reopen-${p.id}`}
                            type="button"
                            className="btn small"
                            aria-expanded={reopening === p.id}
                            onClick={() => {
                              clearNotices();
                              setReopening(reopening === p.id ? null : p.id);
                            }}
                          >
                            {c.reopen}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn small danger"
                          onClick={() => {
                            if (window.confirm(c.confirmDelete(p.name))) void periodAction(() => api.admin.deleteRequestPeriod(p.id), c.deletedNotice(p.name));
                          }}
                        >
                          {c.remove}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {/* Below the table, not inside it: a table row would scroll sideways with the table on a phone. */}
        {reopeningPeriod && today !== null ? (
          <ReopenPanel
            key={reopeningPeriod.id}
            period={reopeningPeriod}
            today={today}
            onCancel={() => {
              setReopening(null);
              // Back to the button that opened the panel, for a keyboard user.
              document.getElementById(`reopen-${reopeningPeriod.id}`)?.focus();
            }}
            onReopened={async (reopened) => {
              setReopening(null);
              setPeriodsNotice({ kind: 'success', text: c.reopenedNotice(reopened.name, formatDayYear(reopened.dueDate)), at: 'table' });
              await refresh();
            }}
          />
        ) : null}
        {periodsNotice?.at === 'table' ? notice : null}
        {formDefaultStart !== null && today !== null ? <PeriodForm defaultStart={formDefaultStart} today={today} error={formError} onOpen={openPeriod} /> : null}
        {periodsNotice?.at === 'form' ? notice : null}
      </section>

      {selectedPeriod ? (
        <section className="card stack" aria-labelledby="review-h">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h2 id="review-h">{c.reviewTitle(selectedPeriod.name)}</h2>
            {selectedPeriod.pendingCount > 0 ? (
              <button type="button" className="btn primary small" onClick={() => void reviewAction(() => api.admin.approveAll(selectedPeriod.id))}>
                {c.approveAll}
              </button>
            ) : null}
          </div>
          {reviewError ? <Notice kind="error">{reviewError}</Notice> : null}
          {review.requests.length === 0 ? <p className="muted">{c.noRequests}</p> : null}
          {review.requests.length > 0 ? (
            <div className="table-region" role="region" aria-label={c.reviewTitle(selectedPeriod.name)} tabIndex={0}>
              <table>
                <thead>
                  <tr>
                    <th>{c.reviewColumns.physician}</th>
                    <th>{c.reviewColumns.week}</th>
                    <th>{c.reviewColumns.kind}</th>
                    <th>{c.reviewColumns.status}</th>
                    <th>{c.reviewColumns.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...review.requests]
                    .sort((a, b) => a.physicianName.localeCompare(b.physicianName) || a.weekStart.localeCompare(b.weekStart))
                    .map((r) => {
                      const entry = entryOver.get(cellKey(r.physicianId, r.weekStart));
                      return (
                        <tr key={r.id}>
                          <td>
                            <strong>{r.physicianName}</strong>
                            {r.physicianId === myPhysicianId ? <span className="badge mine">{c.you}</span> : null}
                          </td>
                          <td>{formatDate(r.weekStart)}</td>
                          <td>
                            <span className={`badge req-${r.kind}`}>{kindsLong[r.kind]}</span>
                          </td>
                          <td>
                            <span className={`badge ${statusBadgeClass(r.status)}`}>{copy.requests.period.status[r.status]}</span>
                            {/* The admin entry counts instead of this request while it is there. */}
                            {entry ? (
                              <div className="entry-over">
                                <span className={`badge entry-${entry}`}>{c.entryOver(kindsLong[entry])}</span>
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <div className="row">
                              {r.status !== 'approved' ? (
                                <button type="button" className="btn small" onClick={() => void reviewAction(() => api.admin.decideRequest(r.id, 'approved'))}>
                                  {c.approve}
                                </button>
                              ) : null}
                              {r.status !== 'declined' ? (
                                <button type="button" className="btn small" onClick={() => void reviewAction(() => api.admin.decideRequest(r.id, 'declined'))}>
                                  {c.decline}
                                </button>
                              ) : null}
                              {r.status !== 'pending' ? (
                                <button type="button" className="btn small" onClick={() => void reviewAction(() => api.admin.decideRequest(r.id, 'pending'))}>
                                  {c.undo}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="btn small danger"
                                onClick={() => {
                                  if (window.confirm(c.confirmRemoveRequest(r.physicianName, formatDate(r.weekStart)))) void reviewAction(() => api.admin.removeRequest(r.id));
                                }}
                              >
                                {c.removeRequest}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="card stack" aria-labelledby="grid-h">
        <h2 id="grid-h">{c.grid}</h2>
        <p className="muted">{c.gridIntro}</p>
        <div className="row" role="group" aria-label={c.range}>
          <div className="field">
            <label htmlFor="grid-start">{c.rangeStart}</label>
            <input id="grid-start" className="input" type="date" value={rangeStart} onChange={(e) => { setRangeStart(e.target.value); }} />
          </div>
          <div className="field">
            <label htmlFor="grid-end">{c.rangeEnd}</label>
            <input id="grid-end" className="input" type="date" value={rangeEnd} onChange={(e) => { setRangeEnd(e.target.value); }} />
          </div>
        </div>
        <div className="legend">
          {(['vacation', 'loa', 'preferred', 'avoid'] as const).map((k) => (
            <span key={k}>
              <span className={`avail-cell mini ${k}`} aria-hidden="true">
                {c.letters[k]}
              </span>{' '}
              {c.legend[k]}
            </span>
          ))}
          <span>
            <span className="avail-cell mini vacation from-request" aria-hidden="true" /> {c.legend.request}
          </span>
          <span>
            <span className="avail-cell mini vacation pending" aria-hidden="true" /> {c.legend.pending}
          </span>
          <span>
            <span className="avail-cell mini declined" aria-hidden="true">
              {c.letters.vacation}
            </span>{' '}
            {c.legend.declined}
          </span>
          <span>
            <span className="avail-cell mini loa over-request" aria-hidden="true">
              {c.letters.loa}
            </span>{' '}
            {c.legend.over}
          </span>
        </div>
        {gridError ? <Notice kind="error">{gridError}</Notice> : null}
        {grid?.physicians.length === 0 ? <p className="muted">{c.noRoster}</p> : null}
        {grid && grid.physicians.length > 0 ? (
          <div className="table-region avail-grid" role="region" aria-label={c.grid} tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th className="sticky-col">{copy.distribution.columns.physician}</th>
                  {grid.weeks.map((w) => (
                    <th key={w.weekStart} className={`week-head ${w.kind !== 'regular' ? w.kind : ''}`} title={describeDays(w)}>
                      <span className="week-head-date">{formatDate(w.weekStart).slice(0, 6)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.physicians.map((p) => {
                  const mine = p.id === myPhysicianId;
                  const who = mine ? c.youName(p.name) : p.name;
                  return (
                    <tr key={p.id}>
                      <th scope="row" className="sticky-col">
                        <span className="physician">
                          <span className="dot" style={{ background: p.colour }} aria-hidden="true" />
                          <span className="physician-name">{p.name}</span>
                          {mine ? <span className="badge mine">{c.you}</span> : null}
                        </span>
                      </th>
                      {grid.weeks.map((w) => {
                        const key = cellKey(p.id, w.weekStart);
                        const state = states.get(key) ?? EMPTY_CELL;
                        const label = c.cellLabel(who, formatRange(w.onCallStart, w.onCallEnd), cellStateText(state));
                        const saving = savingCell === key;
                        return (
                          <td key={w.weekStart} className={w.kind !== 'regular' ? w.kind : ''}>
                            <button
                              type="button"
                              id={cellId(p.id, w.weekStart)}
                              className={cellClass(state)}
                              aria-label={label}
                              aria-haspopup="dialog"
                              aria-busy={saving}
                              disabled={saving}
                              title={label}
                              onClick={() => { openCell(p, w); }}
                            >
                              {state.shown ? c.letters[state.shown.kind] : ''}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <dialog
        ref={dialogRef}
        className="cell-dialog"
        aria-labelledby="cell-editor-title"
        onCancel={(e) => {
          // Escape does not close the window while a change is saving.
          if (savingRef.current) e.preventDefault();
        }}
        onClose={onDialogClosed}
      >
        {editing ? (
          <CellEditor
            physicianName={editingName}
            week={editing.week}
            state={editingState}
            mine={editing.physician.id === myPhysicianId}
            busy={savingCell !== null}
            error={editorError}
            onEntry={chooseEntry}
            onDecide={(requestId, status) => void saveCell(() => api.admin.decideRequest(requestId, status))}
            onClose={closeCell}
          />
        ) : null}
      </dialog>
    </>
  );
}
